/**
 * A transformation matrix.
 * @see https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/transform
 */
export type TransformationMatrix = [number, number, number, number, number, number];

/**
 * Create a transformation matrix that scales and then rotates the drawn content relative to a given
 * origin point.
 */
export function createTransform(options: {
  origin: { x: number; y: number };
  rotation?: number;
  scale?: { x: number; y: number };
}): TransformationMatrix {
  const {
    origin: { x: originX, y: originY },
    rotation = 0,
    scale: { x: scaleX, y: scaleY } = { x: 1, y: 1 },
  } = options;

  return [
    translate(-originX, -originY),
    scale(scaleX, scaleY),
    rotate(rotation),
    translate(originX, originY),
  ].reduce(combineTransforms);
}

function translate(x: number, y: number): TransformationMatrix {
  return [1, 0, 0, 1, x, y];
}

function rotate(degrees: number): TransformationMatrix {
  const radians = degrees * (Math.PI / 180);
  const sin = Math.sin(radians);
  const cos = Math.cos(radians);
  return [cos, sin, -sin, cos, 0, 0];
}

function scale(x: number, y: number): TransformationMatrix {
  return [x, 0, 0, y, 0, 0];
}

/**
 * Create a new transformation matrix that applies the first transform followed by the second
 * transform (the reverse order to typical matrix multiplication).
 */
function combineTransforms(
  first: TransformationMatrix,
  second: TransformationMatrix
): TransformationMatrix {
  const [a, b, c, d, e, f] = first;
  const [g, h, i, j, k, l] = second;
  return [
    a * g + b * i,
    a * h + b * j,
    c * g + d * i,
    c * h + d * j,
    e * g + f * i + k,
    e * h + f * j + l,
  ];
}
